import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash2, UserPlus } from 'lucide-react';
import { TICKET_PRIORITIES, TICKET_TYPES, addParticipant, assignTicket, removeParticipant, setTicketStatus, updateTicket } from '@mkcore/support/schema';
import { Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, Textarea } from '@mkcore/ui';
import { Markdown } from '@/components/markdown';
import { ApiError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { Timeline, TicketPriorityBadge, TicketStatusBadge, ticketQuery, timelineQuery, usePeople, type Message, type Ticket } from '@/features/support/tickets';
import { AgentProposals, Composer, PendingReply } from '@/features/support/replies';
import { SlaBadge, TicketSlaPanel } from '@/features/support/sla';
import { AttachmentsPanel } from '@/features/files/attachments-panel';
import { TicketTime } from '@/features/support/ticket-time';
import { TicketRetainer } from '@/features/support/ticket-retainer';
import { TicketIssue, TicketIssueDialog, useCanMakeIssue } from '@/features/support/ticket-issue';
import { MergeDialog, SplitDialog, TicketLinks } from '@/features/support/ticket-merge';
import { TicketArticles } from '@/features/support/articles';
import { RelatedKnowledge } from '@/features/knowledge/panels';
import { TicketSuggestions } from '@/features/support/suggestions';
import { ItemAgent } from '@/features/agents/item-status';

export const Route = createFileRoute('/_app/support/$id')({ component: TicketPage });

/** Solving asks for the solution for the requester and an optional internal note (CO-0621). */
function SolveDialog({ ticket, onClose }: { ticket: Ticket; onClose: () => void }) {
  const { t } = useTranslation();
  const [solution, setSolution] = useState(ticket.solution);
  const [note, setNote] = useState(ticket.solutionNote);
  const [error, setError] = useState<string | null>(null);
  const solve = useActionMutation(setTicketStatus, { invalidate: [['support']], silentErrors: true, success: t('tickets.solvedDone'), onSuccess: onClose });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('tickets.solveTitle', { key: ticket.key })}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('tickets.fields.solution')} hint={t('tickets.solutionHint')}>
            {(p) => <Textarea {...p} rows={4} value={solution} onChange={(e) => setSolution(e.target.value)} />}
          </Field>
          <Field label={t('tickets.fields.solutionNote')}>{(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!solution.trim()}
            loading={solve.isPending}
            onClick={() => solve.mutate({ input: { id: ticket.id, status: 'solved', solution, solutionNote: note } }, { onError: (e) => setError(e instanceof ApiError ? e.message : String(e)) })}
          >
            {t('tickets.solve')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Type, priority, category and tags of a ticket (CO-0614, CO-0618). */
function Classification({ ticket, locked }: { ticket: Ticket; locked: boolean }) {
  const { t } = useTranslation();
  const [category, setCategory] = useState(ticket.category ?? '');
  const [tags, setTags] = useState(ticket.tags.join(', '));
  const save = useActionMutation(updateTicket, { invalidate: [['support']], success: t('common.saved') });
  const patch = (input: Record<string, unknown>) => save.mutate({ input: { id: ticket.id, ...input }, ifMatch: ticket.version });
  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('tickets.fields.type')}>
          {(p) => (
            <NativeSelect {...p} value={ticket.type} disabled={locked} onChange={(e) => patch({ type: e.target.value })}>
              {TICKET_TYPES.map((v) => (
                <option key={v} value={v}>
                  {t(`tickets.type.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('tickets.fields.priority')}>
          {(p) => (
            <NativeSelect {...p} value={ticket.priority} disabled={locked} onChange={(e) => patch({ priority: e.target.value })}>
              {TICKET_PRIORITIES.map((v) => (
                <option key={v} value={v}>
                  {t(`tickets.priority.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      <Field label={t('tickets.fields.category')}>
        {(p) => <Input {...p} value={category} disabled={locked} onChange={(e) => setCategory(e.target.value)} onBlur={() => category !== (ticket.category ?? '') && patch({ category: category.trim() || null })} />}
      </Field>
      <Field label={t('tickets.fields.tags')} hint={t('tickets.tagsHint')}>
        {(p) => (
          <Input
            {...p}
            value={tags}
            disabled={locked}
            onChange={(e) => setTags(e.target.value)}
            onBlur={() => {
              const list = tags
                .split(',')
                .map((x) => x.trim())
                .filter(Boolean);
              if (list.join(',') !== ticket.tags.join(',')) patch({ tags: list });
            }}
          />
        )}
      </Field>
    </div>
  );
}

/** The requester and who reads along (CO-0608). */
function Participants({ ticket, locked }: { ticket: Ticket; locked: boolean }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const add = useActionMutation(addParticipant, { invalidate: [['support']], onSuccess: () => setEmail('') });
  const remove = useActionMutation(removeParticipant, { invalidate: [['support']] });
  return (
    <section className="grid gap-2" aria-label={t('tickets.participants')}>
      <h3 className="text-sm font-bold">{t('tickets.participants')}</h3>
      <ul className="grid gap-1 text-sm">
        {ticket.participants.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2">
            <span>
              {p.name ? `${p.name} ` : ''}
              <span className="text-fg-3">{p.email}</span>
            </span>
            {!locked && (
              <Button size="icon" variant="ghost" aria-label={t('tickets.removeParticipant', { email: p.email })} onClick={() => remove.mutate({ input: { id: p.id } })}>
                <Trash2 />
              </Button>
            )}
          </li>
        ))}
        {!ticket.participants.length && <li className="text-fg-3">{t('tickets.noParticipants')}</li>}
      </ul>
      {!locked && (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate({ input: { id: ticket.id, email } });
          }}
        >
          <Input aria-label={t('tickets.participantEmail')} type="email" placeholder={t('tickets.participantEmail')} value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" size="sm" variant="secondary" disabled={!email.trim()} loading={add.isPending}>
            <UserPlus /> {t('tickets.addParticipant')}
          </Button>
        </form>
      )}
    </section>
  );
}

/** The ticket workspace (CO-0615): the timeline with the composer under it (CO-0603), and a side panel with client, status and assignee. */
function TicketPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const message = useErrorMessage();
  const me = useMe();
  const canUpdate = useCan('support:update');
  const [solving, setSolving] = useState(false);
  const [proposal, setProposal] = useState<Message | null>(null);
  const [makingIssue, setMakingIssue] = useState(false);
  const [merging, setMerging] = useState(false);
  const [splitting, setSplitting] = useState<Message | null>(null);
  const canMakeIssue = useCanMakeIssue();
  const q = useQuery(ticketQuery(id));
  const timeline = useQuery(timelineQuery(id));
  const people = usePeople({ id: q.data?.assigneeId ?? null, name: q.data?.assigneeName ?? null });
  const status = useActionMutation(setTicketStatus, { invalidate: [['support']] });
  const assign = useActionMutation(assignTicket, { invalidate: [['support']] });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const ticket = q.data;
  const locked = !canUpdate || ticket.status === 'closed';
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
      <dt className="text-fg-3">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
  const setStatus = (to: Ticket['status']) => status.mutate({ input: { id: ticket.id, status: to } });
  return (
    <>
      <Link to="/support" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('tickets.title')}
      </Link>
      <PageHeader
        eyebrow={<span className="tabular">{ticket.key}</span>}
        title={ticket.subject}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
            <SlaBadge sla={ticket.sla} />
            <span className="text-fg-3">
              {t(`tickets.type.${ticket.type}`)} · {t(`tickets.channel.${ticket.channel}`)} · {formatDateTime(ticket.createdAt)}
            </span>
          </span>
        }
        actions={
          !locked && (
            <span className="flex flex-wrap gap-2">
              {ticket.status !== 'open' && ticket.status !== 'solved' && (
                <Button variant="secondary" loading={status.isPending} onClick={() => setStatus('open')}>
                  {t('tickets.toOpen')}
                </Button>
              )}
              {ticket.status !== 'waiting' && ticket.status !== 'solved' && (
                <Button variant="secondary" onClick={() => setStatus('waiting')}>
                  {t('tickets.toWaiting')}
                </Button>
              )}
              {ticket.status === 'solved' && (
                <Button variant="secondary" onClick={() => setStatus('open')}>
                  {t('tickets.reopen')}
                </Button>
              )}
              {ticket.status !== 'solved' && <Button onClick={() => setSolving(true)}>{t('tickets.solve')}</Button>}
              {ticket.status === 'solved' && <Button onClick={() => setStatus('closed')}>{t('tickets.close')}</Button>}
              <Button variant="ghost" onClick={() => setMerging(true)}>
                {t('ticketMerge.open')}
              </Button>
            </span>
          )
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-4">
          {ticket.description && (
            <Card className="p-4">
              <Markdown text={ticket.description} />
            </Card>
          )}
          <TicketLinks ticket={ticket} />
          <TicketSuggestions ticket={ticket} locked={locked} />
          {timeline.data && <Timeline messages={timeline.data.items} onSplit={locked ? undefined : setSplitting} />}
          {ticket.solution && (
            <Card className="grid gap-1 border-success/40 p-4">
              <h2 className="text-sm font-bold">{t('tickets.fields.solution')}</h2>
              <Markdown text={ticket.solution} />
              {ticket.solutionNote && <p className="text-xs text-fg-3">{ticket.solutionNote}</p>}
            </Card>
          )}
          {timeline.data && <PendingReply ticket={ticket} messages={timeline.data.items} />}
          {!locked && timeline.data && <AgentProposals messages={timeline.data.items} onUse={setProposal} />}
          {!locked && timeline.data && (
            <Card className="p-4">
              <Composer key={ticket.id} ticket={ticket} messages={timeline.data.items} proposal={proposal} onProposalTaken={() => setProposal(null)} />
            </Card>
          )}
        </div>
        <div className="grid content-start gap-4">
          <Card className="grid gap-3 p-4">
            <ItemAgent type="ticket" id={ticket.id} />
            <dl className="grid gap-2">
              <Row label={t('tickets.fields.requester')}>
                {ticket.requesterName ?? '–'} {ticket.requesterEmail && <span className="block text-xs text-fg-3">{ticket.requesterEmail}</span>}
              </Row>
              <Row label={t('tickets.fields.organisation')}>
                {ticket.organisationId ? (
                  <Link to="/org/$id" params={{ id: ticket.organisationId }} className="underline">
                    {ticket.organisationName}
                  </Link>
                ) : (
                  <span className="text-fg-3">{t('tickets.noOrganisation')}</span>
                )}
              </Row>
              <Row label={t('tickets.fields.engagement')}>
                {ticket.engagementId ? (
                  <span className="grid gap-1">
                    <Link to="/engagements/$id" params={{ id: ticket.engagementId }} className="underline">
                      {ticket.engagementName}
                    </Link>
                    <TicketRetainer engagementId={ticket.engagementId} />
                  </span>
                ) : (
                  '–'
                )}
              </Row>
              <Row label={t('tickets.fields.project')}>
                {ticket.projectId ? (
                  <Link to="/projects/$id" params={{ id: ticket.projectId }} className="underline">
                    {ticket.projectName}
                  </Link>
                ) : (
                  '–'
                )}
              </Row>
              {ticket.waitingUntil && <Row label={t('tickets.fields.waitingUntil')}>{formatDate(ticket.waitingUntil)}</Row>}
              <Row label={t('ticketIssue.issue')}>
                <TicketIssue ticket={ticket} />
              </Row>
            </dl>
            {!ticket.issueId && !locked && canMakeIssue && (
              <Button size="sm" variant="secondary" className="justify-self-start" onClick={() => setMakingIssue(true)}>
                {t('ticketIssue.open')}
              </Button>
            )}
            <Field label={t('tickets.fields.assignee')}>
              {(p) => (
                <NativeSelect {...p} value={ticket.assigneeId ?? ''} disabled={locked} onChange={(e) => assign.mutate({ input: { id: ticket.id, assigneeId: e.target.value || null } })}>
                  <option value="">{t('tickets.nobody')}</option>
                  {people.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.id === me.id ? t('tickets.me', { name: u.name }) : u.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </Card>
          <Card className="p-4">
            <TicketSlaPanel key={`sla-${ticket.version}`} ticket={ticket} locked={locked} />
          </Card>
          <Card className="p-4">
            <TicketTime key={`time-${ticket.projectId ?? ''}`} ticket={ticket} locked={locked} />
          </Card>
          <Card className="p-4">
            <Classification key={ticket.version} ticket={ticket} locked={locked} />
          </Card>
          <Card className="p-4">
            <Participants ticket={ticket} locked={locked} />
          </Card>
          <Card className="p-4">
            <TicketArticles ticket={ticket} locked={locked} />
          </Card>
          <AttachmentsPanel type="ticket" id={ticket.id} archived={locked} />
          <RelatedKnowledge type="ticket" id={ticket.id} q={ticket.subject} title={t('knowledge.related.earlier')} />
        </div>
      </div>
      {solving && <SolveDialog ticket={ticket} onClose={() => setSolving(false)} />}
      {makingIssue && <TicketIssueDialog ticket={ticket} onClose={() => setMakingIssue(false)} />}
      {merging && <MergeDialog ticket={ticket} onClose={() => setMerging(false)} />}
      {splitting && <SplitDialog message={splitting} onClose={() => setSplitting(null)} />}
    </>
  );
}
